import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { FallbackProps } from 'react-error-boundary';
import { Theme, Button, Typography } from '@branchjs/neo-components';
import * as Sentry from '@sentry/react';

import { ScreenErrorStyles } from './ScreenError.styles';
import { ImageService } from '../../services/ImageService/ImageService';

const DEFAULT_MESSAGE = 'No error message was specified';

const ScreenError = ({ resetErrorBoundary, error }: FallbackProps) => {
  const reportExceptionToSentry = (exception: Error) => {
    Sentry.captureException(exception, { tags: { errorBoundary: true } });
  };

  const handleNonErrorException = (nonErrorException: any) => {
    const message = nonErrorException.message || DEFAULT_MESSAGE;
    reportExceptionToSentry(new Error(message));
  };

  useEffect(() => {
    if (error instanceof Error) {
      reportExceptionToSentry(error);
      return;
    }

    handleNonErrorException(error);
  }, []);

  return (
    <div role="alert" className={ScreenErrorStyles.ContentContainer}>
      <Theme injectFirst>
        <img
          className={ScreenErrorStyles.Image}
          src={ImageService.getImageUrl('/static/images/error/error.svg')}
          alt="Error Browser"
        />
        <Typography className={ScreenErrorStyles.Title} variant="h2">
          Oops! We encountered an error.
        </Typography>
        <Typography className={ScreenErrorStyles.Description} variant="body1">
          Our team has received notification of the issue and is actively working to fix it as soon as possible.
        </Typography>
        <Link to="/">
          <Button variant="contained" color="primary" size="large" onClick={resetErrorBoundary}>
            Back to Homepage
          </Button>
        </Link>
      </Theme>
    </div>
  );
};

export default ScreenError;
